import {
	CreateIssueMessageRequest,
	CreateIssueTrackerFilterRequest,
	IssueTrackerFilter,
	UpdateIssueRequest,
	UpdateIssueTrackerFilterRequest,
} from '@nimbly-technologies/nimbly-common';
import {
	apiURL,
	NIMBLY_API_ISSUES_BULK_EDIT,
	NIMBLY_API_ISSUES_BULK_MESSAGE,
	NIMBLY_API_ISSUES_CUSTOM_FILTER,
} from 'config/baseURL';
import { DeptApprover } from 'core/domain/models/issue/issueApprovalModel';
import { issueApprovalRepository } from 'core/infrastructure/repositories/issue/issueApprovalRepository';
import API, { gatewayFetch } from 'helpers/api';
import i18n from 'i18n';
import { ApprovalDetailsI, ApproversListI } from 'reducers/issues/issues.reducer';
import { LocalizedError } from 'helpers/customError';
import { SavedFilterApiError } from './savedFilterError';

export type BulkEditIssuesData = Pick<
	UpdateIssueRequest,
	'status' | 'priorityV2' | 'severity' | 'dueDate' | 'assignedDepartments' | 'assignedTo'
>;

export type BulkEditIssuesPayloadData = Partial<BulkEditIssuesData>;

export type bulkIssueMessageType = Pick<
	CreateIssueMessageRequest,
	'createdAt' | 'createdBy' | 'createdByName' | 'message' | 'messageType'
> & {
	attachments?: string[];
};

export const bulkEditIssues = async (issueIDs: string[], changedValues: BulkEditIssuesPayloadData) => {
	const url = NIMBLY_API_ISSUES_BULK_EDIT;
	const token = await API.getFirebaseToken();
	const payload = {
		issueIDs,
		data: {
			...changedValues,
			triggerNotification: true,
		},
	};
	const response = await API.put(url, token, payload);
	let json: any;
	try {
		json = await response.json();
	} catch {
		json = {};
	}

	if (response.status !== 200) {
		// Two different kinds of message, so two different kinds of error. A
		// role-denied response has no meaningful string `message`, and the
		// localized fallback is copy we may render as-is. The API's own message
		// is English prose, so it stays a plain `Error` for `helpers/toast` to
		// replace with localized copy rather than show to a non-English user.
		if (typeof json?.message === 'string') {
			throw new Error(json.message);
		}

		throw new LocalizedError(i18n.t('error.issuePage.bulkEditFailed'));
	}
	return json.data;
};

export const bulkSubmitIssueMesage = async (issueIDs: string[], data: bulkIssueMessageType) => {
	const url = NIMBLY_API_ISSUES_BULK_MESSAGE;
	const token = await API.getFirebaseToken();
	const payload = {
		issueIDs,
		data: {
			...data,
			triggerNotification: true,
		},
	};
	const response = await API.post(url, token, payload);
	const json = await response.json();

	if (response.status !== 200) {
		throw json.message;
	}
	return json.data;
};

/**
 * Rejects a saved filter response, preserving the API's machine-readable code.
 *
 * These calls used to `throw json.message`, which dropped everything but the
 * English prose — the reason `savedFilterError` had to match on the message
 * text. `enum` is the field api-issues puts the stable code in.
 *
 * @param json - The parsed non-200 body from a saved filter endpoint.
 * @throws A `SavedFilterApiError` carrying the body's message and code.
 */
const throwSavedFilterError = (json: { message?: unknown; enum?: unknown }): never => {
	throw new SavedFilterApiError(
		typeof json?.message === 'string' ? json.message : '',
		typeof json?.enum === 'string' ? json.enum : undefined,
	);
};

export const getIssueCustomFilter = async (): Promise<IssueTrackerFilter[]> => {
	const url = NIMBLY_API_ISSUES_CUSTOM_FILTER;
	const token = await API.getFirebaseToken();

	const response = await API.get(url, token);
	const json = await response.json();

	if (response.status !== 200) {
		throwSavedFilterError(json);
	}
	return json.data;
};

export const saveIssueCustomFilter = async (payload: CreateIssueTrackerFilterRequest) => {
	const url = NIMBLY_API_ISSUES_CUSTOM_FILTER;
	const token = await API.getFirebaseToken();

	const response = await API.post(url, token, payload);
	const json = await response.json();

	if (response.status !== 200) {
		throwSavedFilterError(json);
	}
	return json.data;
};

export const updateIssueCustomFilter = async (filterId: string, payload: UpdateIssueTrackerFilterRequest) => {
	const url = `${NIMBLY_API_ISSUES_CUSTOM_FILTER}/${filterId}`;
	const token = await API.getFirebaseToken();

	const response = await API.put(url, token, payload);
	const json = await response.json();

	if (response.status !== 200) {
		throwSavedFilterError(json);
	}
	return json.data;
};

export const deleteIssueCustomFilter = async (filterId: string) => {
	const url = `${NIMBLY_API_ISSUES_CUSTOM_FILTER}/${filterId}`;
	const token = await API.getFirebaseToken();

	const response = await API.delete(url, token);
	const json = await response.json();

	if (response.status !== 200) {
		throwSavedFilterError(json);
	}
	return json.data;
};

export const postApprovalRequest = async (
	issueId: string,
	approvalType: string,
	approvers: string[],
	optionalNote: string,
) => {
	const authToken = await API.getFirebaseToken();
	const url = `${apiURL}/issues/issueApproval/create-request`;
	const payload = {
		issueID: issueId,
		approvalType: approvalType,
		approvers: approvers,
		comments: optionalNote,
	};
	const options = {
		method: 'POST',
		headers: {
			Authorization: authToken,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload),
	};

	const response = await gatewayFetch(url, options);

	if (response.status !== 200) return 'FAILED';
	const responseData = await response.json();
	return 'SUCCESS';
};

export const fetchIssueApprovalData = async (issueID: string): Promise<ApprovalDetailsI | undefined> => {
	return issueApprovalRepository.getApprovalData(issueID);
};

export const postApprovalReview = async (
	requestId: string,
	issueId: string,
	status: string,
	comments: string,
	departmentID: string | undefined,
) => {
	return issueApprovalRepository.approveRequest({ requestId, issueId, status, comments, departmentID });
};

export const patchCancelRequest = async (issueId: string): Promise<string> => {
	return issueApprovalRepository.cancelApprovalRequest({ issueId });
};

export const fetchApproverHistoryDetails = async (issueID: string, id: string, type: 'memberID' | 'departmentID') => {
	return issueApprovalRepository.getApproverHistory({ issueId: issueID, id: id, type: type });
};

export const fetchApproversList = async (requesterID: string): Promise<ApproversListI[] | undefined> => {
	return issueApprovalRepository.getApproversList({ requesterId: requesterID });
};

export const patchAddApprover = async (issueID: string, userID: string[] | DeptApprover[]): Promise<string> => {
	return issueApprovalRepository.addApprover({ issueId: issueID, userIds: userID });
};

export const patchRemoveApprover = async (issueID: string, userID: string | DeptApprover) => {
	return issueApprovalRepository.removeApprover({ issueId: issueID, userId: userID });
};
